import { useEffect } from 'react'
import { addListener, type UnsubscribeListener } from '@reduxjs/toolkit'
import { useDispatch } from 'react-redux'
import {
  ERROR_ACTION,
  GET_PAST_VISITS_SUCCESS,
  GET_PRODUCTS_SUCCESS,
  GET_VISIT_SUCCESS,
  LOADING_ACTION,
  ORDER_PRODUCTS_SUCCESS,
} from './productsConstants'
import { type CreatedProduct, type ProductList, type Range } from '../../types/products/productTypes'
import { getErrorMessage } from './productsActions'
import { getPatientChart } from '../charts/ChartsThunk'
import { type PastVisit, type Visit } from '../../types/types'
import { type OrderCategory } from 'components/product-order-popup/common'

type Props = {
  patientId: string
  tabId: string
  isShowing: boolean
  openProducts?: (value: boolean, category?: string, productList?: ProductList[], range?: Range) => void
  showResult: (result: boolean | string) => void
  setProductList: (productList: ProductList[]) => void
  setLoading: (loading: boolean) => void
  setTreatLink: (link: string) => void
  setError: (newError: string) => void
  openVisitsList: (visitList: PastVisit[]) => void
  setSelectedVisit: (visit: Visit) => void
}

type GetPastVisitsSuccessAction = {
  type: typeof GET_PAST_VISITS_SUCCESS
  payload: PastVisit[]
}
type LoadingAction = {
  type: typeof LOADING_ACTION
}
type ErrorAction = {
  type: typeof ERROR_ACTION
}
type GetVisitSuccessAction = {
  type: typeof GET_VISIT_SUCCESS
  payload: Visit
}
type GetProductsSuccessAction = {
  type: typeof GET_PRODUCTS_SUCCESS
  payload: ProductList[]
  extraData: {
    category: string
    patientId: string
    tabId: string
  }
}
type OrderProductSuccessAction = {
  type: typeof ORDER_PRODUCTS_SUCCESS
  payload: CreatedProduct
  extraData: {
    patientId: string
    tabId: string
  }
}
type Actions =
  | GetPastVisitsSuccessAction
  | LoadingAction
  | GetVisitSuccessAction
  | GetProductsSuccessAction
  | OrderProductSuccessAction
  | ErrorAction

export const ProductListener = ({
  patientId,
  isShowing,
  openProducts,
  tabId,
  showResult,
  setProductList,
  setLoading,
  setTreatLink,
  setError,
  openVisitsList,
  setSelectedVisit,
}: Props) => {
  const dispatch = useDispatch()
  useEffect(() => {
    const unsubscribe = dispatch(
      addListener({
        predicate: () => true,
        effect: async (action: Actions) => {
          switch (action.type) {
            case LOADING_ACTION:
              setLoading(true)
              break
            case GET_PAST_VISITS_SUCCESS:
              setLoading(false)
              const list = action.payload?.filter(
                (visit: PastVisit) => visit.Status !== 'cancelledbypatient' && visit.Status !== 'cancelledbyadmin',
              )
              if (list.length) openVisitsList(list)
              else setError('No visits found')
              break
            case GET_VISIT_SUCCESS:
              setSelectedVisit(action.payload)
              setLoading(false)
              break
            case GET_PRODUCTS_SUCCESS:
              if (!action.payload.length) {
                setError('No available products')
              } else if (patientId === action.extraData.patientId && tabId === action.extraData.tabId) {
                const range = getPriceRangeForMoudDeliveryProducts(action.payload, action.extraData.category)
                setProductList(action.payload)
                if (!isShowing) {
                  // @ts-expect-error TS2722 -- MIGRATION: not approved, fix when encountered
                  openProducts(true, action.extraData.category, action.payload, range)
                }
              } else if (patientId === action.extraData.patientId) {
                // @ts-expect-error TS2722 -- MIGRATION: not approved, fix when encountered
                openProducts(false)
              }
              break
            case ORDER_PRODUCTS_SUCCESS:
              getPatientChart(patientId, tabId)

              if (patientId === action.extraData.patientId && tabId === action.extraData.tabId) {
                const createdOrder: CreatedProduct = action.payload
                setLoading(false)
                //
                const encounterUrl = createdOrder.Item?.EncounterUrl || createdOrder.EncounterUrl
                // @ts-expect-error TS2532 -- MIGRATION: not approved, fix when encountered
                const fulfillmentType = createdOrder.Item?.FulfillmentType || createdOrder.Items?.[0].FulfillmentType
                if (encounterUrl) setTreatLink(encounterUrl)
                if (createdOrder.Status === 'pendingpayment') {
                  showResult(false)
                } else if (createdOrder.Total === 0) {
                  if (fulfillmentType === 'treat') {
                    if (encounterUrl) {
                      showResult('order-treat-success')
                    } else {
                      showResult('order-error')
                    }
                  } else showResult('order-success')
                } else if (fulfillmentType === 'treat') {
                  if (encounterUrl) {
                    showResult('payment-treat-success')
                  } else {
                    showResult('payment-treat-error')
                  }
                } else showResult('payment-success')
              }
              break
            case ERROR_ACTION:
              setLoading(false)
              setError(getErrorMessage(action, patientId, tabId))
              break
            default:
              break
          }
        },
      }),
    )

    return unsubscribe as unknown as UnsubscribeListener
  }, [
    showResult,
    patientId,
    tabId,
    openProducts,
    isShowing,
    setSelectedVisit,
    dispatch,
    openVisitsList,
    setError,
    setProductList,
    setLoading,
    setTreatLink,
  ])

  return null
}
function getPriceRangeForMoudDeliveryProducts(
  products: ProductList[],
  category: OrderCategory | (string | {}) /* allow strings but also provide autocompletion for OrderCategory */,
) {
  let From = 0
  let To = 0
  products.forEach((product) => {
    product.Products.forEach((item) => {
      if (item.IsPharmacyPickupAllowed === false && (category === 'moud' || category === 'MenopauseHormoneTherapy')) {
        From = From < item.PriceRange.From && From !== 0 ? From : item.PriceRange.From
        To = To > item.PriceRange.To ? To : item.PriceRange.To
      }
    })
  })
  return { From, To }
}
